import type { EdgeServer, ServerCommandType } from '../types'
import { Lamp } from './Lamp'
import { UsageBar } from './serverStatus'
import { LAMP_TEXT, relativeTime, serverLamp } from './serverLamp'

export type CardStatus = { cpuUsage: number; memoryUsage: number; diskUsage: number }

// 서버 카드 — 대시보드·뷰어 모니터 공용. command 는 Edge팀 화면 전용(원격 모니터링 시작/중지, 미지정이면 숨김)
// 카드 전체가 상세로 가는 링크 — 안쪽 모니터링 버튼은 stopPropagation 으로 분리
export default function ServerCard({ server, status, onClick, showCompany, command }: {
  server: EdgeServer
  status?: CardStatus
  onClick?: () => void
  showCompany?: boolean
  command?: { pendingType?: ServerCommandType; onCommand: (s: EdgeServer, type: ServerCommandType) => void }
}) {
  const lamp = serverLamp(server)
  const totalDevices = (server.deviceConnectedCount ?? 0) + (server.deviceNotConnectedCount ?? 0)
  const hasDevices = server.deviceConnectedCount != null || server.deviceNotConnectedCount != null
  const last = relativeTime(server.lastMachineLogAt)

  return (
    <div role="link" tabIndex={0} onClick={onClick}
      onKeyDown={e => { if (e.key === 'Enter') onClick?.() }}
      className="group bg-surface rounded-xl border border-gray-200 p-4 cursor-pointer hover:border-gray-400 transition-colors">
      <div className="flex items-start gap-3">
        <Lamp state={lamp} className="mt-[7px]" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900 truncate group-hover:text-accent transition-colors">{server.name}</p>
          <p className="text-xs text-gray-500 mt-0.5 truncate">
            {showCompany && <span className="text-gray-600">{server.company?.name ?? '회사 미지정'}<span className="text-gray-300 mx-1.5">/</span></span>}
            {server.ipAddress ? <span className="font-mono">{server.ipAddress}</span> : 'IP 없음'}
          </p>
        </div>
        <span className={`shrink-0 text-xs ${lamp === 'fault' ? 'text-red-600 font-medium' : 'text-gray-500'}`}>{LAMP_TEXT[lamp]}</span>
      </div>

      {status ? (
        <div className="mt-4 space-y-1.5">
          <UsageBar label="CPU" value={status.cpuUsage} />
          <UsageBar label="메모리" value={status.memoryUsage} />
        </div>
      ) : (
        <p className="mt-4 text-xs text-gray-400">자원 사용량을 아직 받지 못했습니다.</p>
      )}

      <div className="flex items-center justify-between gap-3 mt-4 pt-3 border-t border-gray-100 text-xs">
        <div className="flex items-center gap-4 min-w-0 text-gray-500">
          {hasDevices && (
            <span className="shrink-0">
              디바이스 <span className="tabular font-medium text-gray-800">{totalDevices}</span>
              {(server.deviceNotConnectedCount ?? 0) > 0 && (
                <span className="text-red-600 font-medium ml-1.5">이상 {server.deviceNotConnectedCount}</span>
              )}
            </span>
          )}
          <span className="truncate" title={server.lastMachineLogAt ? new Date(server.lastMachineLogAt).toLocaleString('ko-KR') : undefined}>
            {last ? `데이터 ${last}` : '수신 데이터 없음'}
          </span>
        </div>
        {/* 미보고(구버전 LiveEdge) 또는 등록 미완료 서버는 버튼 없음 */}
        {command && server.reportedMonitoringState && server.status === 'Registered' && (
          command.pendingType ? (
            // 명령 진행 중 인디케이터 — 완료(ServerCommandCompleted) 또는 60초 타임아웃까지 재클릭 차단
            <span className="flex items-center gap-1.5 text-gray-500 shrink-0" onClick={e => e.stopPropagation()}>
              <span className="inline-block w-3 h-3 border-2 border-gray-200 border-t-accent rounded-full animate-spin" />
              {command.pendingType === 'StopMonitoring' ? '중지하는 중…' : '시작하는 중…'}
            </span>
          ) : (
            <button
              onClick={e => {
                e.stopPropagation() // 카드 클릭(상세 이동)과 분리
                command.onCommand(server, server.reportedMonitoringState === 'Running' ? 'StopMonitoring' : 'StartMonitoring')
              }}
              disabled={!server.isOnline}
              title={server.isOnline ? undefined : 'Edge가 오프라인이라 명령을 보낼 수 없습니다'}
              className="shrink-0 font-medium px-2 py-1 rounded-md border border-gray-300 text-gray-700 hover:border-gray-500 hover:text-gray-900 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              {server.reportedMonitoringState === 'Running' ? '모니터링 중지' : '모니터링 시작'}
            </button>
          )
        )}
      </div>
    </div>
  )
}
